import {
  Check,
  Phone,
  MessageCircle,
  Calendar,
  MapPin,
  Plane,
  ShirtIcon,
  Sun,
  Heart,
  Video,
  Clock,
  CheckCircle2,
  Award,
  DollarSign,
  Briefcase,
  Mic,
  Sparkles,
  Building2,
  Users,
  TrendingUp,
  Home,
} from 'lucide-react';
import { useEffect, useState } from 'react';

export default function WelcomeDavidH() {
  const [daysUntilRetreat, setDaysUntilRetreat] = useState(0);
  const [daysUntilZoom, setDaysUntilZoom] = useState(0);

  useEffect(() => {
    const retreatDate = new Date('2026-07-23');
    const zoomDate = new Date('2026-06-24');
    const today = new Date();

    setDaysUntilRetreat(Math.ceil((retreatDate - today) / (1000 * 60 * 60 * 24)));
    setDaysUntilZoom(Math.ceil((zoomDate - today) / (1000 * 60 * 60 * 24)));
  }, []);

  const expertiseTopics = [
    {
      icon: Building2,
      title: 'Real Estate Investing',
      desc: 'Frameworks for evaluating deals, building portfolios, and creating long-term equity.',
    },
    {
      icon: DollarSign,
      title: 'Securing Funding',
      desc: 'The essentials of capital — traditional, creative, and what most investors miss.',
    },
    {
      icon: Home,
      title: 'HUD Section 8 Program',
      desc: 'The honest pros, the real cons, and how to operate ethically and profitably.',
    },
    {
      icon: TrendingUp,
      title: 'Working with Private Investors',
      desc: 'How to attract, structure, and protect relationships with private capital.',
    },
    {
      icon: Users,
      title: 'Building a Winning Team',
      desc: 'The team architecture every investor needs to scale beyond solo deals.',
    },
  ];

  const packingList = [
    {
      category: 'Essentials',
      icon: Plane,
      items: [
        'Valid Passport (not expiring within 6 months of travel)',
        'Travel insurance documents',
        'Any required medications',
        'Phone charger & adapter (Panama uses Type A/B plugs)',
        'Printed copy of your facilitator agenda (we will send ahead)',
      ],
    },
    {
      category: 'Facilitator Attire',
      icon: ShirtIcon,
      items: [
        'Business casual for sessions (1–2 outfits)',
        'One elevated outfit for the celebration dinner',
        'Comfortable casual clothes for off-time',
        'Swimwear for beach & pool',
        'Light jacket for air-conditioned rooms',
        'Comfortable walking shoes & sandals',
      ],
    },
    {
      category: 'Tools of the Trade',
      icon: Briefcase,
      items: [
        'Laptop & charger (for slides / case studies)',
        'Notebook & pens for your sessions',
        'Any handouts or deal sheets you want printed (let us know in advance)',
        'Business cards (the brothers will want to stay in touch)',
      ],
    },
    {
      category: 'Wellness',
      icon: Sun,
      items: [
        'Sunscreen (SPF 30+)',
        'Sunglasses & hat',
        'Personal toiletries',
        'Any supplements you take regularly',
      ],
    },
  ];

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-[#0b1530] via-[#101a3a] to-[#070e22]"
      data-testid="welcome-davidh-page"
    >
      {/* Hero */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="max-w-3xl mx-auto text-center relative z-10">
          <p
            className="text-indigo-300 font-sans uppercase tracking-[0.3em] text-sm font-semibold mb-6"
            data-testid="welcome-davidh-eyebrow"
          >
            A Personal Welcome
          </p>

          <h1 className="font-serif text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Thank You, <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-sky-300">
              David.
            </span>
          </h1>

          <p className="text-slate-300 font-sans text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Your seat is confirmed and your role as a
            <span className="text-indigo-300 font-semibold"> wealth & real estate facilitator</span> at
            The Reset is officially set. The men in that room are going to walk away thinking
            differently because of what you bring.
          </p>

          <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
            <div
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center"
              data-testid="countdown-retreat"
            >
              <p className="text-5xl font-bold text-indigo-300 font-mono">{daysUntilRetreat}</p>
              <p className="text-slate-400 font-sans text-sm mt-2">Days Until Panama</p>
            </div>
            <div
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center"
              data-testid="countdown-zoom"
            >
              <p className="text-5xl font-bold text-white font-mono">{daysUntilZoom}</p>
              <p className="text-slate-400 font-sans text-sm mt-2">Days Until Facilitator Zoom</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gratitude */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <div
            className="bg-gradient-to-r from-indigo-500/10 to-sky-500/10 border-l-4 border-indigo-400 rounded-r-2xl p-8 md:p-10"
            data-testid="payment-confirmation"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-indigo-300" />
              </div>
              <p className="text-indigo-300 font-sans uppercase tracking-wider text-sm font-semibold">
                Confirmation Received
              </p>
            </div>
            <p className="text-slate-300 font-sans text-lg leading-relaxed mb-3">
              David, thank you for completing your registration for{' '}
              <span className="text-white font-semibold">The Reset 2026</span>. A confirmation has
              been sent to your email.
            </p>
            <p className="text-slate-300 font-sans text-lg leading-relaxed">
              Bringing your real-estate and wealth-building work into this brotherhood is a gift.
              We're deeply grateful you're walking this journey with us.
            </p>
          </div>
        </div>
      </section>

      {/* Facilitator Confirmation */}
      <section className="py-16 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="w-16 h-16 rounded-2xl bg-indigo-500/20 flex items-center justify-center mx-auto mb-4">
              <Award className="w-8 h-8 text-indigo-300" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2">
              You're Officially a Facilitator
            </h2>
            <p className="text-slate-400 font-sans">
              Honored to have your expertise shaping this brotherhood
            </p>
          </div>

          <div className="bg-[#0b1530] border border-indigo-500/30 rounded-2xl p-8 shadow-xl shadow-indigo-500/5">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                    <Mic className="w-6 h-6 text-indigo-300" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">Role</p>
                    <p className="text-slate-400 font-sans text-sm">
                      Wealth Management Expert & Real Estate Facilitator
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-indigo-300" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">Your Sessions</p>
                    <p className="text-slate-400 font-sans text-sm">
                      Friday Breakout: Building Generational Wealth Through Real Estate
                    </p>
                    <p className="text-slate-400 font-sans text-sm">
                      Saturday Breakout: Funding, Teams & Scaling Your Portfolio
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-6 h-6 text-indigo-300" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">Firm Represented</p>
                    <p className="text-slate-400 font-sans text-sm">
                      Matters of the Mind Vaultage
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <p className="text-indigo-300 font-sans text-sm uppercase tracking-wider font-semibold mb-4">
                  What You'll Bring:
                </p>
                <ul className="space-y-3">
                  {[
                    'Real-world experience as an entrepreneur, developer & investor',
                    'A playbook for building generational wealth',
                    'Honest lessons from the deals — wins and losses',
                    'A vision of what economic ownership looks like for us',
                  ].map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 text-slate-300 font-sans text-sm"
                    >
                      <CheckCircle2 className="w-5 h-5 text-indigo-300 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas of Expertise */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-indigo-300 font-sans uppercase tracking-[0.2em] text-xs font-bold mb-4">
              Your Areas of Expertise
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-3">
              What You'll Be Teaching
            </h2>
            <p className="text-slate-400 font-sans max-w-2xl mx-auto">
              We'll shape your sessions around these pillars — bring the real stories, the real
              numbers, and the real frameworks.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {expertiseTopics.map((topic, idx) => {
              const Icon = topic.icon;
              return (
                <div
                  key={idx}
                  className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-indigo-500/40 hover:bg-white/[0.07] transition-all"
                  data-testid={`expertise-${idx}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-indigo-300" />
                  </div>
                  <h3 className="text-white font-sans font-semibold text-lg mb-2">{topic.title}</h3>
                  <p className="text-slate-400 font-sans text-sm leading-relaxed">{topic.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Honorarium */}
      <section className="py-16 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <div
            className="bg-gradient-to-br from-indigo-500/10 via-indigo-500/5 to-transparent border border-indigo-500/30 rounded-2xl p-8 md:p-10"
            data-testid="honorarium-section"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-7 h-7 text-indigo-300" />
              </div>
              <div>
                <p className="text-indigo-300 font-sans uppercase tracking-wider text-xs font-bold mb-1">
                  Facilitator Honorarium
                </p>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-white">
                  A Token of Our Gratitude
                </h2>
              </div>
            </div>

            <p className="text-slate-300 font-sans text-lg leading-relaxed mb-4">
              In recognition of your contribution as a facilitator, an{' '}
              <span className="text-indigo-300 font-semibold">honorarium will be paid to you
              within 14 business days following the close of the retreat</span>.
            </p>
            <p className="text-slate-300 font-sans text-lg leading-relaxed mb-6">
              We'll confirm payment details (preferred method, mailing address, or wire
              instructions) during our facilitator Zoom call so everything is set well before you
              travel.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                <p className="text-indigo-300 font-sans text-xs uppercase tracking-wider mb-2">
                  When
                </p>
                <p className="text-white font-sans font-semibold">
                  Within 14 business days post-retreat
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                <p className="text-indigo-300 font-sans text-xs uppercase tracking-wider mb-2">
                  How
                </p>
                <p className="text-white font-sans font-semibold">
                  Your preferred method (Zelle, ACH, check)
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                <p className="text-indigo-300 font-sans text-xs uppercase tracking-wider mb-2">
                  Confirmation
                </p>
                <p className="text-white font-sans font-semibold">
                  We'll lock details on the Zoom call
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facilitator Prep Call */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="w-16 h-16 rounded-2xl bg-indigo-500/20 flex items-center justify-center mx-auto mb-4">
              <Video className="w-8 h-8 text-indigo-300" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2">
              Facilitator Prep Call
            </h2>
            <p className="text-slate-400 font-sans">
              Let's align on sessions, logistics, and honorarium details
            </p>
          </div>

          <div className="bg-[#0b1530] border border-indigo-500/30 rounded-2xl p-8 shadow-xl shadow-indigo-500/5">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-indigo-300" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">Wednesday, June 24, 2026</p>
                    <p className="text-slate-500 font-sans text-sm">About a month before we meet</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-indigo-300" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">7:00 PM EST</p>
                    <p className="text-slate-500 font-sans text-sm">About 60 minutes</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                    <Video className="w-6 h-6 text-indigo-300" />
                  </div>
                  <div>
                    <p className="text-white font-sans font-semibold">Via Zoom</p>
                    <p className="text-slate-500 font-sans text-sm">
                      Check your Google email for meeting details
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-6">
                <p className="text-indigo-300 font-sans text-sm uppercase tracking-wider font-semibold mb-4">
                  What We'll Cover:
                </p>
                <ul className="space-y-3">
                  {[
                    'Align on real-estate & wealth topics for your sessions',
                    'Pre-work to send conference attendees ahead of the retreat',
                    'Confirm honorarium payment method & details',
                    'Travel logistics & airport pickup',
                    'Any A/V or material needs for your sessions',
                  ].map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 text-slate-300 font-sans text-sm"
                    >
                      <CheckCircle2 className="w-5 h-5 text-indigo-300 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Pack */}
      <section className="py-16 px-6 bg-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2">
              What to Pack
            </h2>
            <p className="text-slate-400 font-sans">Your facilitator-ready guide for Panama</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {packingList.map((section, index) => {
              const Icon = section.icon;
              return (
                <div
                  key={index}
                  className="bg-[#0b1530]/60 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-indigo-500/30 transition-colors"
                  data-testid={`packing-${section.category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <h3 className="text-indigo-300 font-sans font-semibold mb-4 flex items-center gap-3">
                    <Icon className="w-5 h-5" />
                    {section.category}
                  </h3>
                  <ul className="space-y-3">
                    {section.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-indigo-300 mt-1 flex-shrink-0" />
                        <span className="text-slate-300 font-sans text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Travel Details */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2">
              Travel Details
            </h2>
            <p className="text-slate-400 font-sans">
              Everything you need to get to Panama with ease
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#0b1530] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Plane className="w-6 h-6 text-indigo-300" />
                <h3 className="text-white font-sans font-semibold">Arrival</h3>
              </div>
              <div className="space-y-2 text-slate-300 font-sans">
                <p>
                  <span className="text-slate-500">Date:</span> Thursday, July 23, 2026
                </p>
                <p>
                  <span className="text-slate-500">Airport:</span> Tocumen International (PTY)
                </p>
                <p>
                  <span className="text-slate-500">Arrive by:</span> 9:00 PM (earlier if possible —
                  facilitator briefing dinner)
                </p>
              </div>
              <p className="text-indigo-300 font-sans text-sm mt-4 flex items-center gap-2">
                <Check className="w-4 h-4" />
                Airport shuttle included — share your flight info with us
              </p>
            </div>
            <div className="bg-[#0b1530] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-6 h-6 text-indigo-300" />
                <h3 className="text-white font-sans font-semibold">Where We're Staying</h3>
              </div>
              <div className="space-y-2 text-slate-300 font-sans">
                <p>
                  <span className="text-slate-500">Hotel:</span> The Westin Playa Bonita
                </p>
                <p>
                  <span className="text-slate-500">Location:</span> Panama City, Panama
                </p>
                <p>
                  <span className="text-slate-500">Check-out:</span> Sunday, July 26 by 2:00 PM
                </p>
                <p>
                  <span className="text-slate-500">Distance:</span> ~30 min from PTY airport
                </p>
              </div>
              <a
                href="https://www.marriott.com/search/availabilityCalendar.mi?isRateCalendar=true&propertyCode=PTYBW"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-300 font-sans text-sm hover:underline mt-4 inline-block"
              >
                View Hotel Details →
              </a>
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="bg-[#0b1530] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-6 h-6 text-indigo-300" />
                <h3 className="text-white font-sans font-semibold">Passport & Entry</h3>
              </div>
              <ul className="space-y-2 text-slate-300 font-sans text-sm">
                <li>U.S. citizens do not need a visa for stays under 180 days.</li>
                <li>Passport must be valid for at least 6 months past travel dates.</li>
                <li>Have proof of onward/return travel ready at immigration.</li>
              </ul>
            </div>
            <div className="bg-[#0b1530] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Sun className="w-6 h-6 text-indigo-300" />
                <h3 className="text-white font-sans font-semibold">On the Ground</h3>
              </div>
              <ul className="space-y-2 text-slate-300 font-sans text-sm">
                <li>Currency: USD is widely accepted alongside Panamanian Balboa.</li>
                <li>Weather: 80–88°F, humid with brief afternoon showers.</li>
                <li>Power: Type A/B plugs, 120V (same as U.S.).</li>
                <li>Language: Spanish (English widely spoken at hotel & airport).</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 px-6 bg-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
            Got Questions, David?
          </h2>
          <p className="text-slate-400 font-sans mb-8">
            Anything at all — sessions, honorarium, travel — reach out anytime.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:804-310-7433"
              className="flex items-center gap-3 bg-indigo-400 text-[#0b1530] px-8 py-4 rounded-full font-sans font-semibold hover:bg-indigo-300 transition-all hover:-translate-y-0.5 shadow-lg shadow-indigo-500/25"
              data-testid="call-btn"
            >
              <Phone className="w-5 h-5" />
              Call: (804) 310-7433
            </a>
            <a
              href="sms:804-310-7433"
              className="flex items-center gap-3 bg-transparent border-2 border-indigo-400 text-indigo-300 px-8 py-4 rounded-full font-sans font-semibold hover:bg-indigo-500/10 transition-all"
              data-testid="text-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>

          <p className="text-slate-500 font-sans text-sm mt-6">
            Or email:{' '}
            <a
              href="mailto:askcoachneil@gmail.com"
              className="text-indigo-300 hover:underline"
            >
              askcoachneil@gmail.com
            </a>
          </p>
        </div>
      </section>

      {/* Final Message */}
      <section className="py-20 px-6 bg-gradient-to-t from-indigo-500/10 to-transparent">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-indigo-500/20 flex items-center justify-center mx-auto mb-6">
            <Heart className="w-8 h-8 text-indigo-300" />
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
            Thank You for Saying Yes
          </h2>
          <p className="text-slate-300 font-sans text-lg leading-relaxed mb-8">
            David, the brothers in that room have been told for too long that ownership isn't for
            them. You're going to change that. Thank you for showing up to teach, share, and pour
            into this community.
          </p>
          <p className="text-indigo-300 font-serif text-2xl italic">
            "Wealth is built when one of us turns around and teaches the next."
          </p>
          <p className="text-slate-500 font-sans text-sm mt-6">
            — With deep gratitude, the Reset team
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-600 font-sans text-sm">
            July 23–26, 2026 · Panama City, Panama · The Westin Playa Bonita
          </p>
        </div>
      </footer>
    </div>
  );
}
